import { get, post, put } from '@/lib/api/client'
import type { ApiResponse, PaginatedResponse, Region, RegionDetail, MemberCountryRegion, Country, CountryFilterParams, RegionFilterParams, UpdateRegionStatus } from '@/lib/api/types'

export const regionService = {
  // Get all regions with filter
  getAllRegions: async (filters?: RegionFilterParams): Promise<ApiResponse<PaginatedResponse<Region>>> => {
    return await post<ApiResponse<PaginatedResponse<Region>>>('regions/private/filter', filters || {})
  },

  // Get member country regions (for member countries page)
  getMemberCountryRegions: async (): Promise<ApiResponse<MemberCountryRegion[]>> => {
    return await get<ApiResponse<MemberCountryRegion[]>>('member-countries/private/regions')
  },

  // Get region by ID (private endpoint)
  getRegionById: async (regionId: string): Promise<ApiResponse<RegionDetail>> => {
    return await get<ApiResponse<RegionDetail>>(`regions/private/${regionId}`)
  },

  // Get countries by region (deprecated - use countryService.getCountries instead)
  getCountriesByRegion: async (regionId: string): Promise<ApiResponse<Country[]>> => {
    return await get<ApiResponse<Country[]>>(`regions/${regionId}/countries`)
  },

  // Update region status
  updateRegionStatus: async (
    regionId: string,
    data: UpdateRegionStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `regions/admin/private/${regionId}/status`,
      data
    )
  },

  // Get published regions count
  getPublishedRegionsCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('regions/private/stats/published')
  },

  // Get draft regions count
  getDraftRegionsCount: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>('regions/private/stats/draft')
  }
}

export const countryService = {
  // Get all countries (private endpoint with full details)
  getAllCountries: async (): Promise<ApiResponse<Country[]>> => {
    return await get<ApiResponse<Country[]>>('countries/private')
  },

  // Get countries with filters (regionTag in JSON body, page and size as query params)
  getCountries: async (filters?: CountryFilterParams): Promise<ApiResponse<PaginatedResponse<Country>>> => {
    const params = new URLSearchParams()
    if (filters?.page) {
      params.append('page', filters.page.toString())
    }
    if (filters?.size) {
      params.append('size', filters.size.toString())
    }
    const queryString = params.toString()
    const endpoint = queryString ? `countries/private?${queryString}` : 'countries/private'
    
    // Send regionTag in JSON body, page and size as query params
    const body = filters?.regionTag ? { regionTag: filters.regionTag } : {}
    return await post<ApiResponse<PaginatedResponse<Country>>>(endpoint, body)
  },

  // Get country by ID
  getCountryById: async (countryId: string): Promise<ApiResponse<Country>> => {
    return await get<ApiResponse<Country>>(`countries/${countryId}`)
  },

  // Search countries
  searchCountries: async (
    searchBy?: string
  ): Promise<ApiResponse<Country[]>> => {
    const queryParams = new URLSearchParams()
    
    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<Country[]>>(
      `countries/private/search?${queryParams.toString()}`
    )
  }
}
